import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { MediaAsset, Playlist, PlaylistItem, Screen } from '@ghostscreen/contracts';
import { ConfirmDialog } from '../../components/dashboard/ConfirmDialog';
import { apiErrorMessage } from '../../i18n';
import { api } from '../../lib/api';
import { defaultPlaylistDurationSeconds } from '../content/buildContentLibrary';
import { isMediaUsableInPlaylist } from '../content/mediaOperatorStatus';
import { playlistDrawerSummary } from './buildPlaylistListModel';
import { PlaylistDrawerAddMedia } from './PlaylistDrawerAddMedia';
import { PlaylistDrawerItems } from './PlaylistDrawerItems';
import { PlaylistDrawerScreens } from './PlaylistDrawerScreens';

type PlaylistDrawerProps = {
  organisationId: string;
  playlist: Playlist;
  mediaAssets: MediaAsset[];
  screens: Screen[];
  assignmentByScreenId: ReadonlyMap<string, string | null>;
  /** When true (new playlist), focus + select the rename field. */
  autoFocusName?: boolean;
  onClose: () => void;
  onDeleted: () => void;
};

export function PlaylistDrawer({
  organisationId,
  playlist,
  mediaAssets,
  screens,
  assignmentByScreenId,
  autoFocusName = false,
  onClose,
  onDeleted,
}: PlaylistDrawerProps) {
  const { t } = useTranslation(['playlists', 'common']);
  const queryClient = useQueryClient();
  const nameInputRef = useRef<HTMLInputElement>(null);
  const [name, setName] = useState(playlist.name);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    setName(playlist.name);
  }, [playlist.id, playlist.name]);

  useEffect(() => {
    if (!autoFocusName) return;
    const input = nameInputRef.current;
    if (!input) return;
    input.focus();
    input.select();
  }, [autoFocusName, playlist.id]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [onClose]);

  const itemsQuery = useQuery({
    queryKey: ['playlist-items', playlist.id],
    queryFn: () => api.listPlaylistItems(organisationId, playlist.id),
  });
  const publishStateQuery = useQuery({
    queryKey: ['playlist-publish-state', playlist.id],
    queryFn: () => api.getPlaylistPublishState(organisationId, playlist.id),
  });

  const items = itemsQuery.data ?? [];
  const mediaById = useMemo(
    () => new Map(mediaAssets.map((asset) => [asset.id, asset])),
    [mediaAssets],
  );
  const readyMedia = useMemo(
    () => mediaAssets.filter((asset) => isMediaUsableInPlaylist(asset)),
    [mediaAssets],
  );
  const assignedScreenIds = useMemo(() => {
    const ids = new Set<string>();
    for (const [screenId, playlistId] of assignmentByScreenId) {
      if (playlistId === playlist.id) ids.add(screenId);
    }
    return ids;
  }, [assignmentByScreenId, playlist.id]);

  const invalidate = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['playlist-items', playlist.id] }),
      queryClient.invalidateQueries({ queryKey: ['playlist-publish-state', playlist.id] }),
      queryClient.invalidateQueries({ queryKey: ['playlists', organisationId] }),
      queryClient.invalidateQueries({ queryKey: ['screens', organisationId] }),
      queryClient.invalidateQueries({ queryKey: ['screen-assignment', organisationId] }),
    ]);
  };

  const rename = useMutation({
    mutationFn: (next: string) =>
      api.updatePlaylist(organisationId, playlist.id, { name: next.trim() }),
    onSuccess: async () => {
      setFeedback(null);
      await queryClient.invalidateQueries({ queryKey: ['playlists', organisationId] });
    },
    onError: (err) =>
      setFeedback(apiErrorMessage(err, t('drawer.errors.rename'))),
  });

  const addItem = useMutation({
    mutationFn: (asset: MediaAsset) =>
      api.addPlaylistItem(organisationId, playlist.id, {
        mediaAssetId: asset.id,
        durationSeconds: defaultPlaylistDurationSeconds(asset),
      }),
    onSuccess: async () => {
      setFeedback(null);
      await invalidate();
    },
    onError: (err) =>
      setFeedback(apiErrorMessage(err, t('drawer.errors.add'))),
  });

  const removeItem = useMutation({
    mutationFn: (itemId: string) => api.removePlaylistItem(organisationId, playlist.id, itemId),
    onSuccess: async () => {
      setFeedback(null);
      await invalidate();
    },
    onError: (err) =>
      setFeedback(apiErrorMessage(err, t('drawer.errors.remove'))),
  });

  const nudgeDuration = useMutation({
    mutationFn: ({ item, delta }: { item: PlaylistItem; delta: number }) => {
      const next = Math.min(300, Math.max(5, Math.round(item.durationSeconds + delta)));
      return api.updatePlaylistItem(organisationId, playlist.id, item.id, {
        durationSeconds: next,
      });
    },
    onSuccess: async () => {
      setFeedback(null);
      await invalidate();
    },
    onError: (err) =>
      setFeedback(apiErrorMessage(err, t('drawer.errors.duration'))),
  });

  const reorder = useMutation({
    mutationFn: (itemIds: string[]) =>
      api.reorderPlaylistItems(organisationId, playlist.id, { itemIds }),
    onSuccess: async (nextItems) => {
      queryClient.setQueryData(['playlist-items', playlist.id], nextItems);
      setFeedback(null);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['playlist-publish-state', playlist.id] }),
        queryClient.invalidateQueries({ queryKey: ['playlists', organisationId] }),
      ]);
    },
    onError: (err) =>
      setFeedback(apiErrorMessage(err, t('drawer.errors.reorder'))),
  });

  const toggleScreen = useMutation({
    mutationFn: async ({ screen, currentlyOn }: { screen: Screen; currentlyOn: boolean }) => {
      if (currentlyOn) {
        await api.clearScreenAssignment(organisationId, screen.id);
        return;
      }
      await api.assignPlaylistToScreen(organisationId, screen.id, playlist.id);
    },
    onSuccess: async () => {
      setFeedback(null);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['screen-assignment', organisationId] }),
        queryClient.invalidateQueries({ queryKey: ['screens', organisationId] }),
      ]);
    },
    onError: (err) =>
      setFeedback(apiErrorMessage(err, t('drawer.errors.screen'))),
  });

  const publish = useMutation({
    mutationFn: () => api.publishPlaylist(organisationId, playlist.id),
    onSuccess: async () => {
      setFeedback(null);
      await invalidate();
    },
    onError: (err) =>
      setFeedback(apiErrorMessage(err, t('drawer.errors.publish'))),
  });

  const removePlaylist = useMutation({
    mutationFn: () => api.deletePlaylist(organisationId, playlist.id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['playlists', organisationId] });
      onDeleted();
    },
    onError: (err) =>
      setFeedback(apiErrorMessage(err, t('drawer.errors.delete'))),
  });

  const busy =
    addItem.isPending ||
    removeItem.isPending ||
    nudgeDuration.isPending ||
    reorder.isPending ||
    toggleScreen.isPending ||
    publish.isPending ||
    removePlaylist.isPending ||
    rename.isPending;

  const moveItem = (itemId: string, direction: -1 | 1) => {
    const ids = items.map((item) => item.id);
    const index = ids.indexOf(itemId);
    const target = index + direction;
    if (index < 0 || target < 0 || target >= ids.length) return;
    const next = [...ids];
    const [moved] = next.splice(index, 1);
    next.splice(target, 0, moved!);
    reorder.mutate(next);
  };

  const commitName = () => {
    const trimmed = name.trim();
    if (!trimmed || trimmed === playlist.name) {
      setName(playlist.name);
      return;
    }
    rename.mutate(trimmed);
  };

  const unpublished = Boolean(publishStateQuery.data?.hasUnpublishedChanges);
  const canPublish = items.length > 0 && unpublished && !busy;

  return (
    <>
      <div
        className="fixed inset-0 z-40"
        style={{ background: 'rgba(4,4,6,0.58)' }}
        onClick={onClose}
        aria-hidden
      />

      <aside
        role="dialog"
        aria-modal="true"
        aria-label={t('drawer.label', { name: playlist.name })}
        className="fixed bottom-0 end-0 top-0 z-[41] flex w-[min(540px,100vw)] flex-col border-s shadow-[-24px_0_60px_rgba(0,0,0,0.35)] rtl:shadow-[24px_0_60px_rgba(0,0,0,0.35)]"
        style={{
          background: 'var(--gs-panel)',
          borderColor: 'var(--gs-line)',
          animation: 'gs-slide 220ms ease both',
        }}
      >
        <div
          className="flex items-center gap-3 border-b px-[22px] py-4"
          style={{ borderColor: 'var(--gs-line)' }}
        >
          <input
            ref={nameInputRef}
            value={name}
            onChange={(event) => setName(event.target.value)}
            onBlur={commitName}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.currentTarget.blur();
              }
            }}
            aria-label={t('drawer.nameLabel')}
            title={t('drawer.renameTitle')}
            placeholder={t('drawer.namePlaceholder')}
            className="-ms-2 min-w-0 flex-1 rounded-lg border border-transparent px-2 py-1.5 text-xl font-extrabold tracking-[-0.02em] outline-none hover:border-[var(--gs-line)] focus:border-[var(--gs-accent)]"
            style={{
              color: 'var(--gs-ink)',
              background: 'transparent',
            }}
          />
          <button
            type="button"
            title={t('common:actions.close')}
            onClick={onClose}
            className="grid shrink-0 place-items-center p-1"
            style={{ color: 'var(--gs-muted)' }}
          >
            <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
              <path d="m6 6 12 12M18 6 6 18" />
            </svg>
          </button>
        </div>

        <div className="flex flex-1 flex-col gap-[30px] overflow-y-auto px-[22px] py-6">
          <section className="flex flex-col gap-3">
            <SectionHeading
              title={t('drawer.sections.items.title')}
              subtitle={t('drawer.sections.items.subtitle')}
            />
            {itemsQuery.isPending ? (
              <p className="m-0 text-sm" style={{ color: 'var(--gs-muted)' }}>
                {t('drawer.loadingItems')}
              </p>
            ) : (
              <PlaylistDrawerItems
                items={items}
                mediaById={mediaById}
                busy={busy}
                onNudgeDuration={(item, delta) => nudgeDuration.mutate({ item, delta })}
                onMove={moveItem}
                onRemove={(itemId) => removeItem.mutate(itemId)}
              />
            )}
          </section>

          <section className="flex flex-col gap-3">
            <SectionHeading
              title={t('drawer.sections.add.title')}
              subtitle={t('drawer.sections.add.subtitle')}
            />
            <PlaylistDrawerAddMedia
              assets={readyMedia}
              busy={busy}
              onAdd={(asset) => addItem.mutate(asset)}
            />
          </section>

          <section className="flex flex-col gap-3">
            <SectionHeading
              title={t('drawer.sections.screens.title')}
              subtitle={t('drawer.sections.screens.subtitle')}
            />
            <PlaylistDrawerScreens
              screens={screens}
              assignedScreenIds={assignedScreenIds}
              busy={busy}
              onToggle={(screen, currentlyOn) =>
                toggleScreen.mutate({ screen, currentlyOn })
              }
            />
          </section>

          {feedback ? (
            <p className="m-0 text-sm" style={{ color: 'var(--gs-bad)' }} role="alert">
              {feedback}
            </p>
          ) : null}
        </div>

        <div
          className="flex flex-wrap items-center gap-2 border-t px-4 py-3.5 sm:gap-3 sm:px-[22px]"
          style={{ borderColor: 'var(--gs-line)' }}
        >
          <span className="flex min-w-0 flex-[1_1_100%] flex-col gap-0.5 sm:flex-[1_1_auto]">
            <span className="text-[13.5px] font-bold" style={{ color: 'var(--gs-ink)' }}>
              {playlistDrawerSummary(items)}
            </span>
            <span className="text-[12.5px]" style={{ color: 'var(--gs-muted)' }}>
              {unpublished ? t('drawer.unpublished') : t('drawer.saved')}
            </span>
          </span>

          <button
            type="button"
            disabled={busy}
            onClick={() => setConfirmDelete(true)}
            className="rounded-[10px] border px-3.5 py-2.5 text-[13.5px] font-semibold disabled:opacity-50"
            style={{ color: 'var(--gs-bad)', borderColor: 'var(--gs-line)', background: 'none' }}
          >
            {t('common:actions.delete')}
          </button>

          {canPublish ? (
            <button
              type="button"
              disabled={busy}
              onClick={() => publish.mutate()}
              className="rounded-[10px] border px-3.5 py-2.5 text-[13.5px] font-bold disabled:opacity-50"
              style={{
                color: 'var(--gs-ink)',
                borderColor: 'var(--gs-line)',
                background: 'var(--gs-panel2)',
              }}
            >
              {publish.isPending ? t('drawer.publishing') : t('drawer.publish')}
            </button>
          ) : null}

          <button
            type="button"
            onClick={onClose}
            className="min-w-0 flex-1 rounded-[10px] px-[22px] py-[11px] text-sm font-bold sm:flex-none"
            style={{
              color: 'var(--gs-accent-ink)',
              background: 'var(--gs-accent-solid)',
            }}
          >
            {t('common:actions.done')}
          </button>
        </div>
      </aside>

      {confirmDelete ? (
        <ConfirmDialog
          title={t('drawer.deleteDialog.title')}
          description={t('drawer.deleteDialog.description', { name: playlist.name })}
          confirmLabel={t('common:actions.delete')}
          tone="danger"
          confirming={removePlaylist.isPending}
          onCancel={() => {
            if (!removePlaylist.isPending) setConfirmDelete(false);
          }}
          onConfirm={() => removePlaylist.mutate()}
        />
      ) : null}
    </>
  );
}

function SectionHeading({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <span className="flex flex-col gap-0.5">
      <span
        className="text-[17px] font-extrabold tracking-[-0.015em]"
        style={{ color: 'var(--gs-ink)' }}
      >
        {title}
      </span>
      <span className="text-[13.5px]" style={{ color: 'var(--gs-muted)' }}>
        {subtitle}
      </span>
    </span>
  );
}
